import ExcelJS from "exceljs";
import { uploadReportFile } from "./uploadUtils.js";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

export const loadImageAsBase64 = async (url) => {
  return new Promise(async (resolve) => {
    const token = localStorage.getItem("auth_token"); // 👈 unificado
    const response = await fetch(
      `${API_URL}/proxy?url=${encodeURIComponent(url)}`,
      { headers: token ? { "Authorization": `Bearer ${token}` } : {} }
    );

    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      resolve(null);
      return;
    }

    const blob = await response.blob();
    const img = new Image();
    img.src = URL.createObjectURL(blob);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;
      const maxWidth = 250;
      const maxHeight = 250;
      if (width > height && width > maxWidth) {
        height *= maxWidth / width;
        width = maxWidth;
      } else if (height > maxHeight) {
        width *= maxHeight / height;
        height = maxHeight;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const base64 = canvas.toDataURL("image/jpeg");
      resolve(base64);
    };

    img.onerror = (err) => {
      console.error("Error al cargar la imagen", err);
      resolve(null);
    };
  });
};

export const generateExcel = async ({ properties, reportName, setLoadingReport, wizardData, options = { download: true, upload: true } }) => {
  setLoadingReport(true);
  const { download = true, upload = true } = options;
  try {
    const workbook = new ExcelJS.Workbook();

    // Cover Sheet
    const coverSheet = workbook.addWorksheet("Portada");
    coverSheet.addRow(["Informe de Propiedad"]);
    coverSheet.addRow([]);
    coverSheet.addRow(["Generado el:", new Date().toLocaleString()]);

    // Comparison Sheet
    const comparisonSheet = workbook.addWorksheet("Comparativa", { properties: { defaultColWidth: 18 } });
    comparisonSheet.addRow(["Dato", ...properties.map((_, i) => `Prop. ${i + 1}`)]);

    const imageRow = ["Imagen"];
    const imagePromises = properties.map((p) => loadImageAsBase64(p.image_url));
    const base64Images = await Promise.all(imagePromises);
    base64Images.forEach((img) => imageRow.push(img ? "Sí" : "N/A"));
    comparisonSheet.addRow(imageRow);

    const linkRow = ["Link a la propiedad"];
    properties.forEach((p) => linkRow.push(p.url ? { text: "Link", hyperlink: p.url } : "N/A"));
    comparisonSheet.addRow(linkRow);

    const mapsRow = ["Google Maps"];
    properties.forEach((p) => mapsRow.push(p.lat && p.lng ? { text: "Ver Mapa", hyperlink: `https://www.google.com/maps?q=${p.lat},${p.lng}` } : "N/A"));
    comparisonSheet.addRow(mapsRow);

    const priceRow = ["Precio"];
    properties.forEach((p) => priceRow.push(`${p.price_currency || ""} ${p.price || ""}`));
    comparisonSheet.addRow(priceRow);

    const expensasRow = ["Expensas"];
    properties.forEach((p) => expensasRow.push(`${p.expenses_currency || ""} ${p.expenses_value || ""}`));
    comparisonSheet.addRow(expensasRow);

    const valM2Row = ["Valor por m²"];
    properties.forEach((p) => valM2Row.push(p.price && p.square_meters_total ? `${p.price_currency || ""} ${(p.price / p.square_meters_total).toFixed(2)}` : "N/A"));
    comparisonSheet.addRow(valM2Row);

    const dirRow = ["Dirección"];
    properties.forEach((p) => dirRow.push(p.address || ""));
    comparisonSheet.addRow(dirRow);

    const supTotalRow = ["Superficie Total"];
    properties.forEach((p) => supTotalRow.push(`${p.square_meters_total || ""} m²`));
    comparisonSheet.addRow(supTotalRow);

    const supCubRow = ["Superficie Cubierta"];
    properties.forEach((p) => supCubRow.push(`${p.square_meters_covered || ""} m²`));
    comparisonSheet.addRow(supCubRow);

    const dormRow = ["Dormitorios"];
    properties.forEach((p) => dormRow.push(p.bedrooms || ""));
    comparisonSheet.addRow(dormRow);

    const banosRow = ["Baños"];
    properties.forEach((p) => banosRow.push(p.bathrooms || ""));
    comparisonSheet.addRow(banosRow);

    const jardinRow = ["Jardín"];
    properties.forEach((p) => jardinRow.push(p.hasGarden ? "Sí" : "No"));
    comparisonSheet.addRow(jardinRow);

    const piletaRow = ["Pileta"];
    properties.forEach((p) => piletaRow.push(p.hasPool ? "Sí" : "No"));
    comparisonSheet.addRow(piletaRow);

    const parrillaRow = ["Parrilla"];
    properties.forEach((p) => parrillaRow.push(p.hasGrill ? "Sí" : "No"));
    comparisonSheet.addRow(parrillaRow);

    const terrazaRow = ["Terraza"];
    properties.forEach((p) => terrazaRow.push(p.hasTerrace ? "Sí" : "No"));
    comparisonSheet.addRow(terrazaRow);

    const cocherasRow = ["Cocheras"];
    properties.forEach((p) => cocherasRow.push(p.parking || ""));
    comparisonSheet.addRow(cocherasRow);

    const antigRow = ["Antigüedad"];
    properties.forEach((p) => antigRow.push(`${p.antiquity || ""} años`));
    comparisonSheet.addRow(antigRow);

    const opRow = ["Operación"];
    properties.forEach((p) => opRow.push(p.typeOperation || ""));
    comparisonSheet.addRow(opRow);

    const propRow = ["Propiedad"];
    properties.forEach((p) => propRow.push(p.typeProperty || ""));
    comparisonSheet.addRow(propRow);

    const diasRow = ["Días Publicado"];
    properties.forEach((p) => diasRow.push(p.days_published || ""));
    comparisonSheet.addRow(diasRow);

    const visitasRow = ["Visitas"];
    properties.forEach((p) => visitasRow.push(p.views || ""));
    comparisonSheet.addRow(visitasRow);

    const inmoRow = ["Inmobiliaria"];
    properties.forEach((p) => inmoRow.push(p.owner_type || ""));
    comparisonSheet.addRow(inmoRow);
    comparisonSheet.addRow(['Anuncios del inmueble', ...properties.map(p => p.publication_count ?? 1)]);
    comparisonSheet.addRow(['Portales', ...properties.map(p => (p.portals || [p.source || '']).join(' · '))]);
    const ads = workbook.addWorksheet('Anuncios por inmueble');
    ads.addRow(['Comparable', 'ID anuncio', 'Inmobiliaria', 'Portal', 'Moneda', 'Precio del anuncio', 'Última verificación', 'Enlace', 'Usado en el reporte']);
    properties.forEach((p, i) => (p.publications || []).forEach(ad => ads.addRow([
      i + 1, ad.property_id, ad.owner_type || '', ad.source, ad.price_currency, ad.price_value,
      ad.last_seen_at || '', ad.url || '', ad.property_id === p.id ? 'Sí' : '',
    ])));

    for (let i = 0; i < base64Images.length; i++) {
      const base64 = base64Images[i];
      if (base64) {
        const base64Content = base64.split(",")[1] || "";
        const imageId = workbook.addImage({ base64: base64Content, extension: "jpeg" });
        comparisonSheet.addImage(imageId, { tl: { col: i + 1, row: 1 }, br: { col: i + 2, row: 2 } });
      }
    }

    const totalCols = 1 + properties.length;
    const columns = Array.from({ length: totalCols }, () => ({ width: 15 }));
    comparisonSheet.columns = columns;

    comparisonSheet.getRow(1).height = 30;
    comparisonSheet.getRow(2).height = 60;
    comparisonSheet.getRow(23).height = 60;
    comparisonSheet.getRow(8).height = 72.4;

    for (let r = 3; r <= comparisonSheet.rowCount; r++) {
      if (r !== 8 && r !== 23) comparisonSheet.getRow(r).height = 30;
    }

    comparisonSheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.font = { name: "Calibri", size: 11 };
        cell.border = {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        };
      });
    });

    const headerRow = comparisonSheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "eeeeee" } };
      cell.font = { bold: true };
    });

    const firstCol = comparisonSheet.getColumn(1);
    firstCol.eachCell((cell) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "eeeeee" } };
      cell.font = { bold: true };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    // Descargar (si corresponde)
    if (download) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Asegurar extensión .xlsx — el reportName por default no la incluye.
      const baseName = reportName || "property_report";
      a.download = /\.xlsx$/i.test(baseName) ? baseName : `${baseName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }

    // Subir (si corresponde) — devolvemos { url, expires_at } para que el
    // caller arme los botones de share.
    let uploadResult = null;
    if (upload) {
      const r = await uploadReportFile(blob, reportName, wizardData.session_id, 'excel');
      uploadResult = { url: r?.url, expires_at: r?.expires_at };
    }
    return uploadResult;

  } catch (error) {
    console.error("Error generando Excel", error);
    throw error;
  } finally {
    setLoadingReport(false);
  }
};
